import { Badge, Box, IconButton } from "@mui/material";
import { Menu as MenuIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import {
  useSidebar,
  MOBILE_TOPBAR_HEIGHT,
} from "../../../contexts/SidebarContext";
import { useSafeNavigate } from "../../../hooks/useSafeNavigate";
import {
  useTopBarLeadingOutlet,
  useTopBarSlotOutlet,
} from "../../../contexts/TopBarSlotContext";
import { isNative } from "../../../lib/platform";
import { hapticSelection } from "../../../lib/haptics";
import { markNavStart } from "../../../lib/navPerf";
import { useRecentNotificationCount } from "../../Notifications/hooks/useRecentNotificationCount";
import {
  isReportPath,
  REPORT_BACK_FALLBACK,
} from "../Earnings/earningsNavigation";

const MobileTopBar = () => {
  const { toggleMobileDrawer } = useSidebar();
  const navigate = useSafeNavigate();
  const location = useLocation();
  const slot = useTopBarSlotOutlet();
  const leading = useTopBarLeadingOutlet();
  const hasRecentNotifications = useRecentNotificationCount() > 0;

  // The main-tab home screens are navigation roots — there is nowhere to go
  // back to. Everything else (tickers, company earnings, portfolios, and the
  // drawer destinations like Projections and Compare) is reached from
  // somewhere, so on native the burger gives way to a back button there; the
  // drawer stays reachable via the More tab.
  const params = new URLSearchParams(location.search);
  const isRootScreen =
    location.pathname === "/dashboard" ||
    (location.pathname === "/charts" && !params.has("ticker")) ||
    (location.pathname === "/chat" && !params.has("c")) ||
    (location.pathname === "/screener" &&
      !params.has("run") &&
      !params.has("preset") &&
      !params.has("builder") &&
      !params.has("q") &&
      !params.has("f")) ||
    (location.pathname === "/earnings" && !params.has("ticker")) ||
    (location.pathname === "/super-investors" &&
      !params.has("tab") &&
      !params.has("ticker"));
  const showBack = isNative() && !isRootScreen;

  const handleBack = () => {
    hapticSelection();
    markNavStart("back_button");
    // A report opened from a link has no history behind it: Back goes to the hub.
    const depth = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (depth === 0 && isReportPath(location.pathname)) {
      navigate(REPORT_BACK_FALLBACK.to, {
        replace: true,
        state: REPORT_BACK_FALLBACK.state,
      });
      return;
    }
    navigate(-1);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        // Notch/status-bar clearance in the native app; env() resolves to 0
        // in ordinary browsers so the web layout is unchanged
        height: `calc(${MOBILE_TOPBAR_HEIGHT}px + env(safe-area-inset-top, 0px))`,
        paddingTop: "env(safe-area-inset-top, 0px)",
        background: (theme) =>
          theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
        borderBottom: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        zIndex: 1100,
      }}
    >
      {/* Left: back button on native nested screens, burger on web (the web
          shell has no bottom tab bar, so the burger is its only way into the
          drawer). The empty spacer keeps the right slot balanced on native
          root screens. */}
      {showBack && (
        <IconButton
          onClick={handleBack}
          disableRipple
          sx={{
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.9)"
                : "rgba(0,0,0,0.8)",
            // Instant pressed feedback — MUI's ripple is delayed 80ms on
            // touch, which makes a back tap read as ignored.
            WebkitTapHighlightColor: "transparent",
            transition: "transform 0.1s ease, background-color 0.1s ease",
            "&:active": {
              transform: "scale(0.9)",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
            },
          }}
          aria-label="Go back"
        >
          <ArrowBackIcon />
        </IconButton>
      )}
      {/* A root screen's own left-hand control (the dashboard's previous-page
          link) takes the back button's place; an empty spacer keeps the
          centred logo balanced when there is none. */}
      {isNative() && !showBack && leading}
      {isNative() && !showBack && !leading && <Box sx={{ minWidth: 40 }} />}
      {!isNative() && (
        <IconButton
          onClick={toggleMobileDrawer}
          sx={{
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.9)"
                : "rgba(0,0,0,0.8)",
          }}
          aria-label="Open navigation menu"
        >
          {/* New notifications wait behind the drawer's profile menu, so the
              way into the drawer carries the dot. */}
          <Badge variant="dot" color="primary" invisible={!hasRecentNotifications}>
            <MenuIcon />
          </Badge>
        </IconButton>
      )}

      {/* Logo */}
      <Box
        onClick={() => {
          hapticSelection();
          navigate("/dashboard");
        }}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1,
          padding: 1,
        }}
      >
        {/* All-white lockup in the native app, green-and-white on web (Ben,
            2026-07-31). This bar is the app's own chrome on native and sits
            directly under the status bar, where a flat monochrome mark reads
            as part of the system furniture; the coloured version competes with
            the content below it. */}
        <img
          src={
            isNative()
              ? "/insightLongLogoWhite.svg"
              : "/insightLongLogoPrimaryWhiteText.svg"
          }
          alt="Insight Analytics Logo"
          style={{
            height: 28,
            width: "auto",
          }}
        />
      </Box>

      {/* Right slot — pages (e.g. /chat) inject controls here via TopBarSlot.
          Min-width keeps the absolutely-centred logo balanced when empty. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.5,
          minWidth: 40,
        }}
      >
        {slot}
      </Box>
    </Box>
  );
};

export default MobileTopBar;
